import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';

import { useTranslation } from '@apple/lib/i18next';
import { useBackOrNavigate } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DetailsLayout } from '@apple/ui/layouts/DetailsLayout';

import { AnnouncementForm } from '@apple/features/announcements/components/AnnouncementForm';
import { useAnnouncementManagement } from '@apple/features/announcements/hooks/useAnnouncementManagement';
import type { Announcement } from '@apple/features/announcements/models/management';
import { announcementQueries } from '@apple/features/announcements/queries/management';
import { requireAuth } from '@apple/features/auth/utils/require-auth';

export const Route = createFileRoute('/_authed/_admin/content/announcements_/add')({
	component: ManageAnnouncementRoute,
	beforeLoad: args => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.ContentManagement',
		});

		return {
			announcementsQueryOptions: announcementQueries.getAllAnnouncements(),
		};
	},
	loader: ({ context: { queryClient, announcementsQueryOptions } }) => {
		void queryClient.prefetchQuery(announcementsQueryOptions);
	},
	wrapInSuspense: true,
});

function ManageAnnouncementRoute() {
	const goBackToList = useBackOrNavigate();
	const { announcementsQueryOptions } = Route.useRouteContext();
	const { t } = useTranslation('content');
	const announcementsQuery = useSuspenseQuery(announcementsQueryOptions);
	const { add } = useAnnouncementManagement();

	const activeAllowed = useMemo(() => {
		const activeAnnouncement = announcementsQuery.data.find(x => x.active);
		return !activeAnnouncement;
	}, [announcementsQuery.data]);

	const handleSave = useCallback(
		async (announcement: Announcement) => {
			await add.mutateAsync(announcement, {
				onSuccess: () => {
					void goBackToList('/content/announcements');
				},
			});
		},
		[add, goBackToList],
	);

	return (
		<DetailsLayout title={t('announcements.add.title')}>
			<AnnouncementForm
				activeAllowed={activeAllowed}
				onSave={handleSave}
				loading={add.isPending}
				submitted={add.isSuccess}
			/>
		</DetailsLayout>
	);
}
